import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { FaqBlock, ResultCard, ReservedResultsArea, SubHubGrid, buildBreadcrumbLd } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, useAnalyzeGate, todayISO } from "@/components/age-calculator/premium";
import { AnchorToc, AnchorSection, buildAppFaqLd, chronoDiff, CopyResultButton } from "@/components/age-calculator/sub-shell";

const TITLE = "Passport & Visa Form Age Calculator: Copy-Ready Y/M/D";
const DESC = "Generate an exact age in years, months and days for passport, visa and immigration forms with one-click copy — no rounding, no leap-year drift, embassy-friendly formatting.";

const FAQS = [
  { q: "How is age calculated on a passport application?", a: "Passports use chronological age on the form-submission date — years, months and days. Both India (Passport Seva) and Pakistan (NADRA) reject 'approximate' ages." },
  { q: "Are minors under 18 processed differently?", a: "Yes. Minors (under 18) need parental consent, use a shorter validity (5 years in India, 5 in Pakistan) and often waive the police verification step." },
  { q: "Which age applies for a US B1/B2 visa interview?", a: "Age on the interview appointment date. Under-14 and over-79 applicants are usually exempted from the in-person interview." },
  { q: "Can I use this age on a Schengen visa form?", a: "Yes. Schengen forms ask for date of birth only — the Y/M/D breakdown is useful for the accompanying cover letter and financial-declaration templates." },
];

const TOC = [
  { id: "calculator", label: "Calculator" },
  { id: "categories", label: "Age Categories" },
  { id: "faqs", label: "FAQs" },
];

export const Route = createFileRoute("/age-calculator/passport-visa-form")({
  head: () => leafHead({
    path: "/age-calculator/passport-visa-form", title: TITLE, description: DESC,
    scripts: [
      buildAppFaqLd({ name: "Passport & Visa Age Calculator", description: DESC, faqs: FAQS }),
      buildBreadcrumbLd([
        { name: "Home", url: "https://drhint.com/" },
        { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        { name: "Passport & Visa", url: "https://drhint.com/age-calculator/passport-visa-form" },
      ]),
    ],
  }),
  component: Page,
});

function Page() {
  const [dob, setDob] = useState("");
  const [formDate, setFormDate] = useState(todayISO());
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const r = useMemo(() => (dob ? chronoDiff(dob, formDate) : null), [dob, formDate]);
  const category = r ? (r.years < 18 ? "Minor" : r.years >= 60 ? "Senior Citizen" : "Adult") : "";
  const ymd = r ? `${r.years} years, ${r.months} months, ${r.days} days` : "";
  const iso = r ? `${r.years}-${String(r.months).padStart(2, "0")}-${String(r.days).padStart(2, "0")}` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Passport & Visa Form
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Professional, clean UI for filling <strong>passport, visa aur immigration forms</strong> — copy-ready Y/M/D exact age with zero rounding. Detailed date maths ke liye <Link to="/age-calculator" className="text-primary underline">core calculator</Link>.
        </p>
      </section>
      <AnchorToc items={TOC} />

      <section id="calculator" className="mx-auto max-w-5xl px-4 mt-4 scroll-mt-24">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <TripleDateInput label="Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} max={todayISO()} />
            <TripleDateInput label="Form Submission Date" value={formDate} onChange={(v) => { setFormDate(v); reset(); }} quickChips />
          </div>
          <AnalyzeButton onClick={trigger} disabled={!dob || analyzing} label="Generate Form Age" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={260}>
            {revealed && r ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Age (Y/M/D)" value={`${r.years}y ${r.months}m ${r.days}d`} />
                  <ResultCard label="Long form" value={ymd} />
                  <ResultCard label="ISO breakdown" value={iso} sub="YYYY-MM-DD elapsed" />
                  <ResultCard label="Applicant Category" value={category} sub={category === "Minor" ? "Parental consent required" : category === "Senior Citizen" ? "Simplified verification" : "Standard adult processing"} />
                </div>
                <div className="mt-6 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-card p-5">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Ready-to-paste for embassy form</div>
                  <div className="rounded-lg bg-background border border-border px-4 py-3 font-mono text-sm select-all">
                    Age on {formDate}: {ymd}
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <CopyResultButton text={`Age on ${formDate}: ${ymd}`} label="Copy Full Sentence" />
                    <CopyResultButton text={`${r.years} / ${r.months} / ${r.days}`} label="Copy Y/M/D" />
                    <CopyResultButton text={`${r.years}`} label="Copy Years Only" />
                  </div>
                </div>
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter DOB and form date to generate a copy-ready age.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <AnchorSection id="categories" title="Age Categories on Global Immigration Forms">
        <ul>
          <li><strong>Minor (0-17):</strong> Parental consent, shorter passport validity, no separate ID proof in many jurisdictions.</li>
          <li><strong>Adult (18-59):</strong> Standard photograph, biometric enrolment, full financial documentation.</li>
          <li><strong>Senior Citizen (60+):</strong> India & UK allow tatkal / expedited service. US B1/B2 waives interview for 80+.</li>
          <li><strong>Child under 5:</strong> Photograph rules relax slightly; biometrics not mandatory in many countries.</li>
        </ul>
      </AnchorSection>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />
    </Layout>
  );
}